import { useEffect, useMemo, useState } from "react";
import type React from "react";
import {
  AlertTriangle,
  Check,
  Clipboard,
  Loader2,
  Minus,
  PackageOpen,
  Plus,
  RefreshCw,
  Send,
  Share2,
  ShoppingBag,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useParams } from "../../lib/router";
import { createPublicTienditaOrder, createPublicTienditaOrderCheckout, createPublicTienditaQuote, getPublicTienditaCatalog, getPublicTienditaOrderStatus } from "./tienditaApi";

export type PublicTienditaProduct = {
  id: string;
  name: string;
  price: number;
  description?: string;
  imageUrl?: string;
  category?: string;
  sku?: string;
  tags?: string[];
  available?: boolean;
  stock?: number | null;
};

export type PublicTienditaStoreData = {
  slug: string;
  name: string;
  description?: string;
  whatsappNumber: string;
  currency?: string;
  logoUrl?: string;
  coverImageUrl?: string;
  address?: string;
  deliveryNotes?: string;
  status?: "active" | "paused" | "draft";
  products: PublicTienditaProduct[];
  updatedAt?: string;
};

export type PublicTienditaCustomer = {
  name: string;
  phone: string;
  address: string;
  notes: string;
  deliveryMethod: "delivery" | "pickup";
  marketingOptIn: boolean;
};

export type PublicTienditaOrderItem = {
  productId: string;
  name: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
};

export type PublicTienditaOrderPayload = {
  slug: string;
  customer: PublicTienditaCustomer;
  items: PublicTienditaOrderItem[];
  totals: {
    subtotal: number;
    total?: number;
    currency: string;
  };
  whatsappMessage: string;
};

export type PublicTienditaStoreClient = {
  getStoreBySlug: (slug: string) => Promise<PublicTienditaStoreData>;
  submitOrder?: (payload: PublicTienditaOrderPayload) => Promise<{ orderId?: string } | void>;
};

type PublicTienditaStoreProps = {
  slug?: string;
  store?: PublicTienditaStoreData;
  client?: PublicTienditaStoreClient;
  loadStore?: (slug: string) => Promise<PublicTienditaStoreData>;
  submitOrder?: (payload: PublicTienditaOrderPayload) => Promise<{ orderId?: string } | void>;
  onOrderReady?: (payload: PublicTienditaOrderPayload) => void;
};

type LoadState = "idle" | "loading" | "ready" | "empty" | "error";

const defaultCustomer: PublicTienditaCustomer = {
  name: "",
  phone: "",
  address: "",
  notes: "",
  deliveryMethod: "delivery",
  marketingOptIn: false,
};

export function PublicTienditaStore({
  slug,
  store: providedStore,
  client,
  loadStore,
  submitOrder,
  onOrderReady,
}: PublicTienditaStoreProps) {
  const params = useParams<{ slug: string }>();
  const routeSlug = slug || params.slug || "";
  const [store, setStore] = useState<PublicTienditaStoreData | undefined>(providedStore);
  const [loadState, setLoadState] = useState<LoadState>(providedStore ? "ready" : "idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [cart, setCart] = useState<Record<string, number>>({});
  const [customer, setCustomer] = useState<PublicTienditaCustomer>(defaultCustomer);
  const [category, setCategory] = useState("Todos");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const [shareState, setShareState] = useState<"idle" | "copied" | "failed">("idle");
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "sent" | "error">("idle");
  // Specific submit error messages (e.g. rate limit, stock-gone, network).
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  const [serverOrderMessage, setServerOrderMessage] = useState("");
  const [serverOrderMeta, setServerOrderMeta] = useState<Record<string, string>>({});
  const [serverQuote, setServerQuote] = useState<Record<string, number | string | boolean> | undefined>(undefined);
  const [quoteError, setQuoteError] = useState("");
  const [quoteState, setQuoteState] = useState<"idle" | "loading" | "ready" | "error">("idle");

  const resolver = loadStore || client?.getStoreBySlug || loadStoreFromApi;
  const submitter = submitOrder || client?.submitOrder || submitOrderToApi;
  const currency = store?.currency || "COP";
  const activeProducts = useMemo(
    () => (store?.products || []).filter((product) => product.available !== false),
    [store?.products],
  );
  const categories = useMemo(() => {
    const names = activeProducts.map((product) => product.category).filter(Boolean) as string[];
    return ["Todos", ...Array.from(new Set(names))];
  }, [activeProducts]);
  const visibleProducts = useMemo(
    () => activeProducts.filter((product) => category === "Todos" || product.category === category),
    [activeProducts, category],
  );
  const items = useMemo(() => {
    return Object.entries(cart)
      .map(([productId, quantity]) => {
        const product = activeProducts.find((candidate) => candidate.id === productId);
        if (!product || quantity <= 0) return undefined;
        return {
          productId: product.id,
          name: product.name,
          quantity,
          unitPrice: product.price,
          lineTotal: product.price * quantity,
        };
      })
      .filter(Boolean) as PublicTienditaOrderItem[];
  }, [activeProducts, cart]);
  const subtotal = items.reduce((sum, item) => sum + item.lineTotal, 0);
  const quotedSubtotal = Number(serverQuote?.subtotal ?? subtotal);
  const quotedDelivery = Number(serverQuote?.deliveryFee || 0);
  const quotedTax = Number(serverQuote?.tax || 0);
  const quotedDiscount = Number(serverQuote?.discount || 0);
  const finalTotal = Number(serverQuote?.total ?? subtotal);
  const hasQuoteAdjustments = Boolean(serverQuote && (quotedDelivery || quotedTax || quotedDiscount || finalTotal !== subtotal));
  const orderPayload = store
    ? {
        slug: store.slug,
        customer,
        items,
        totals: { subtotal, total: finalTotal, currency },
        whatsappMessage: buildWhatsappMessage(store, customer, items, finalTotal),
      }
    : undefined;
  const activeWhatsappMessage = serverOrderMessage || orderPayload?.whatsappMessage || "";
  const whatsappLink = store && activeWhatsappMessage ? buildWhatsappLink(store.whatsappNumber, activeWhatsappMessage) : "";
  const hasMerchantWhatsApp = Boolean(store?.whatsappNumber?.replace(/[^\d]/g, ""));
  const addressRequired = customer.deliveryMethod === "delivery";
  const missingAddress = addressRequired && !customer.address.trim();
  // Light client-side validation to give immediate feedback. Server is the
  // authoritative validator — these checks just keep obvious mistakes
  // (empty name, malformed phone) from costing a roundtrip.
  const trimmedPhone = customer.phone.trim();
  const normalizedPhone = trimmedPhone.replace(/[^\d+]/g, "");
  const phoneLooksValid = /^\+?\d{7,15}$/.test(normalizedPhone);
  const phoneError = trimmedPhone && !phoneLooksValid
    ? "Revisa el celular: solo números, mínimo 7 dígitos."
    : "";
  const trimmedName = customer.name.trim();
  const nameError = trimmedName && trimmedName.length < 2
    ? "Escribe tu nombre completo (al menos 2 letras)."
    : "";
  const canSubmit = Boolean(
    orderPayload
      && items.length
      && trimmedName.length >= 2
      && trimmedPhone
      && phoneLooksValid
      && !missingAddress,
  );
  const canCopyMessage = canSubmit && submitState === "sent";
  const canOpenWhatsApp = canCopyMessage && Boolean(whatsappLink) && hasMerchantWhatsApp;

  useEffect(() => {
    setStore(providedStore);
    if (providedStore) setLoadState("ready");
  }, [providedStore]);

  useEffect(() => {
    if (providedStore || !routeSlug || !resolver) return;
    let alive = true;
    setLoadState("loading");
    setErrorMessage("");
    resolver(routeSlug)
      .then((nextStore) => {
        if (!alive) return;
        setStore(nextStore);
        setLoadState(nextStore.products.length ? "ready" : "empty");
      })
      .catch((error: unknown) => {
        if (!alive) return;
        console.error("Public Tiendita store load failed", error);
        setErrorMessage("No encontramos esta tienda. Revisa el link o pídele al negocio que lo genere de nuevo.");
        setLoadState("error");
      });
    return () => {
      alive = false;
    };
  }, [providedStore, resolver, routeSlug]);

  const reloadStore = () => {
    if (!routeSlug || !resolver) return;
    setLoadState("loading");
    setErrorMessage("");
    resolver(routeSlug)
      .then((nextStore) => {
        setStore(nextStore);
        setLoadState(nextStore.products.length ? "ready" : "empty");
      })
      .catch((error: unknown) => {
        console.error("Public Tiendita store reload failed", error);
        setErrorMessage("No encontramos esta tienda. Revisa el link o pídele al negocio que lo genere de nuevo.");
        setLoadState("error");
      });
  };

  const updateQuantity = (product: PublicTienditaProduct, delta: number) => {
    setCart((current) => {
      const currentQuantity = current[product.id] || 0;
      const maxStock = typeof product.stock === "number" ? product.stock : Number.POSITIVE_INFINITY;
      const nextQuantity = Math.max(0, Math.min(maxStock, currentQuantity + delta));
      const nextCart = { ...current };
      if (nextQuantity === 0) delete nextCart[product.id];
      else nextCart[product.id] = nextQuantity;
      return nextCart;
    });
  };

  const clearCart = () => setCart({});

  useEffect(() => {
    setServerOrderMessage("");
    setServerOrderMeta({});
    if (submitState === "sent") setSubmitState("idle");
  }, [cart, customer.address, customer.deliveryMethod, customer.marketingOptIn, customer.name, customer.notes, customer.phone]);

  useEffect(() => {
    if (!store?.slug || !items.length) {
      setServerQuote(undefined);
      setQuoteState("idle");
      return;
    }
    let alive = true;
    setQuoteState("loading");
    const timer = window.setTimeout(() => {
      createPublicTienditaQuote(store.slug, {
        customerName: customer.name,
        customerPhone: customer.phone,
        deliveryAddress: customer.deliveryMethod === "delivery" ? customer.address : "Recoge en tienda",
        deliveryMethod: customer.deliveryMethod,
        items: items.map((item) => ({
          productId: item.productId,
          name: item.name,
          quantity: item.quantity,
          price: item.unitPrice,
        })),
      })
        .then((result) => {
          if (!alive) return;
          const quote = result.quote || {};
          setServerQuote({
            subtotal: Number(quote.subtotalCents || 0) / 100,
            deliveryFee: Number(quote.deliveryFeeCents || 0) / 100,
            tax: Number(quote.taxCents || 0) / 100,
            discount: Number(quote.discountCents || 0) / 100,
            total: Number(quote.totalCents || 0) / 100,
            currency: String(quote.currency || currency),
            available: quote.available !== false,
          });
          setQuoteState("ready");
        })
        .catch(async (err: unknown) => {
          if (!alive) return;
          const data = await (err as any).json?.().catch(() => ({}));
          setQuoteError(data?.error?.message || data?.message || "No pudimos validar el total final; stock puede estar agotado.");
          setServerQuote(undefined);
          setQuoteState("error");
        });
    }, 350);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [customer.address, customer.deliveryMethod, customer.name, customer.phone, items, store?.slug]);

  const copyMessage = async () => {
    if (!orderPayload || !canCopyMessage) return;
    try {
      await navigator.clipboard.writeText(activeWhatsappMessage);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
    window.setTimeout(() => setCopyState("idle"), 1800);
  };

  const shareStore = async () => {
    const shareUrl = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: store?.name || "Tienda TienditaBot", url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
      }
      setShareState("copied");
    } catch {
      setShareState("failed");
    }
    window.setTimeout(() => setShareState("idle"), 1800);
  };

  const handleOrder = async () => {
    if (!orderPayload || !canSubmit) return;
    onOrderReady?.(orderPayload);
    if (!submitter) {
      setSubmitState("sent");
      return;
    }
    setSubmitErrorMessage("");
    try {
      setSubmitState("submitting");
      const result = await submitter(orderPayload);
      if (result && typeof result === "object" && "whatsappMessage" in result) {
        setServerOrderMessage(String((result as { whatsappMessage?: unknown }).whatsappMessage || ""));
      }
      if (result && typeof result === "object") {
        const response = result as {
          orderId?: unknown;
          order?: { id?: unknown; paymentLink?: unknown };
          paymentLink?: unknown;
          orderStatusUrl?: unknown;
          paymentInstructions?: unknown;
          provider?: unknown;
          checkoutStatus?: unknown;
          paymentCheckoutError?: unknown;
        };
        setServerOrderMeta({
          orderId: String(response.order?.id || response.orderId || ""),
          paymentLink: String(response.paymentLink || response.order?.paymentLink || ""),
          orderStatusUrl: String(response.orderStatusUrl || ""),
          paymentInstructions: String(response.paymentInstructions || ""),
          provider: String(response.provider || ""),
          checkoutStatus: String(response.checkoutStatus || ""),
          paymentCheckoutError: String(response.paymentCheckoutError || ""),
        });
      }
      setSubmitState("sent");
    } catch (err) {
      // Try to extract specific feedback from common shapes the API throws.
      const candidate = err as { status?: number; statusCode?: number; code?: string; message?: string; error?: string; response?: { status?: number; data?: { error?: string; code?: string } } };
      const status = Number(candidate?.status || candidate?.statusCode || candidate?.response?.status || 0);
      const code = String(candidate?.code || candidate?.response?.data?.code || "");
      const message = String(candidate?.message || candidate?.error || candidate?.response?.data?.error || "");
      let friendly = message || "No pudimos enviar el pedido. Intenta de nuevo en unos segundos.";
      if (status === 429) friendly = "Estás enviando pedidos muy seguido. Espera unos segundos y reintenta.";
      else if (status === 409 || code === "idempotency_in_flight") friendly = "Ya estamos procesando tu pedido. Espera un momento antes de reintentar.";
      else if (status === 503 || code === "payments_unavailable") friendly = "El medio de pago no está disponible ahora. Reintenta en unos minutos.";
      else if (status === 400 && /stock|inventario|agot/i.test(message)) friendly = "Uno o más productos quedaron sin stock. Ajusta tu carrito y reintenta.";
      else if (status === 400) friendly = message || "Revisa los datos del pedido (nombre, celular, dirección).";
      else if (status === 401 || status === 403) friendly = "No tienes permiso para completar esta acción.";
      else if (status >= 500) friendly = "Tuvimos un problema en nuestro lado. Reintenta en unos segundos.";
      setSubmitErrorMessage(friendly);
      setSubmitState("error");
    }
  };

  if (!routeSlug && !providedStore) {
    return (
      <StoreShell>
        <EmptyState
          icon={<PackageOpen className="h-10 w-10" />}
          title="Link de tienda incompleto"
          body="Este enlace no tiene el identificador de la tienda. Pídele al negocio que te comparta de nuevo su link o código QR."
        />
      </StoreShell>
    );
  }

  if (!providedStore && !resolver) {
    return (
      <StoreShell>
        <EmptyState
          icon={<AlertTriangle className="h-10 w-10" />}
          title="Tienda no disponible"
          body="No pudimos cargar el catálogo en este momento. Intenta de nuevo o contacta al negocio por su canal habitual."
        />
      </StoreShell>
    );
  }

  if (loadState === "loading" || loadState === "idle") {
    return (
      <StoreShell>
        <div className="flex min-h-[70vh] items-center justify-center">
          <div className="rounded-[2rem] border border-emerald-100/20 bg-[#141921]/80 p-8 text-center shadow-2xl backdrop-blur">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#4ade80]" />
            <p className="mt-4 text-sm font-black uppercase tracking-[0.22em] text-[#4a556b]">Cargando vitrina</p>
          </div>
        </div>
      </StoreShell>
    );
  }

  if (loadState === "error" || !store) {
    return (
      <StoreShell>
        <EmptyState
          actionLabel="Reintentar"
          body={errorMessage || "La tienda no respondió correctamente. Intenta de nuevo en unos segundos."}
          icon={<AlertTriangle className="h-10 w-10" />}
          onAction={reloadStore}
          title="No pudimos abrir esta tienda"
        />
      </StoreShell>
    );
  }

  const isPaused = store.status === "paused" || store.status === "draft";
  const emptyCatalog = activeProducts.length === 0;

  return (
    <StoreShell>
      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[minmax(0,1fr)_24rem] lg:py-10">
        <main className="space-y-6">
          <section className="relative overflow-hidden rounded-[2.5rem] border border-[#1e2838]/40 bg-[#141921] p-6 text-white shadow-2xl sm:p-8">
            {store.coverImageUrl ? (
              <img alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" src={store.coverImageUrl} />
            ) : null}
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#fbbf24]/25 blur-3xl" />
            <div className="absolute -bottom-24 left-10 h-72 w-72 rounded-full bg-[#4ade80]/20 blur-3xl" />
            <div className="relative flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  {store.logoUrl ? (
                    <img alt={`Logo de ${store.name}`} className="h-16 w-16 rounded-3xl border border-white/30 object-cover shadow-xl" src={store.logoUrl} />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-3xl border border-white/20 bg-[#141921]/15">
                      <ShoppingBag className="h-8 w-8" />
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4ade80]/80">Tienda pública TienditaBot</p>
                    <h1 className="text-4xl font-black tracking-tight sm:text-5xl">{store.name}</h1>
                  </div>
                </div>
                <p className="max-w-2xl text-base leading-8 text-[#4ade80]/90">
                  {store.description || "Arma tu pedido, revisa el resumen y envíalo por WhatsApp en un solo mensaje."}
                </p>
                <div className="flex flex-wrap gap-2 text-xs font-bold uppercase tracking-[0.16em]">
                  <Badge>{activeProducts.length} productos</Badge>
                  {store.address ? <Badge>{store.address}</Badge> : null}
                  {store.deliveryNotes ? <Badge>{store.deliveryNotes}</Badge> : null}
                  {isPaused ? <Badge tone="warning">Tienda en pausa</Badge> : <Badge tone="success">Aceptando pedidos</Badge>}
                </div>
              </div>
              <Button
                className="h-12 rounded-full bg-[#fbbf24] px-6 font-black text-[#0f1420] hover:bg-[#fbbf24]/90"
                onClick={shareStore}
                type="button"
              >
                <Share2 className="mr-2 h-4 w-4" />
                {shareState === "copied" ? "Link copiado" : shareState === "failed" ? "No se pudo copiar" : "Compartir"}
              </Button>
            </div>
          </section>

          {isPaused ? (
            <div className="rounded-[1.5rem] border border-[#fbbf24]/40 bg-[#2a2a18] p-4 text-sm font-semibold text-[#fbbf24]">
              Esta tienda está temporalmente en pausa. Puedes preparar el pedido, pero confirma disponibilidad por WhatsApp antes de pagar o desplazarte.
            </div>
          ) : null}

          {emptyCatalog ? (
            <EmptyState
              body="El comercio todavía no publicó productos disponibles. Vuelve más tarde o escribe por WhatsApp si ya tienes una referencia."
              icon={<PackageOpen className="h-10 w-10" />}
              title="Catálogo vacío"
            />
          ) : (
            <>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {categories.map((name) => (
                  <button
                    className={`shrink-0 rounded-full border px-4 py-2 text-sm font-black transition ${
                      category === name
                        ? "border-[#4ade80] bg-[#4ade80] text-[#0f1420]"
                        : "border-[#1e2838] bg-[#141921] text-[#6b7a8d] hover:border-[#4ade80]/50"
                    }`}
                    key={name}
                    onClick={() => setCategory(name)}
                    type="button"
                  >
                    {name}
                  </button>
                ))}
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {visibleProducts.map((product) => {
                  const quantity = cart[product.id] || 0;
                  const soldOut = product.available === false || product.stock === 0;
                  return (
                    <Card className="overflow-hidden rounded-[1.75rem] border-[#1e2838] bg-[#141921] shadow-sm" key={product.id}>
                      {product.imageUrl ? (
                        <img alt={product.name} className="h-44 w-full object-cover" src={product.imageUrl} />
                      ) : (
                        <div className="flex h-44 w-full items-center justify-center bg-gradient-to-br from-[#1a3a25] via-[#2a2a18] to-[#1e2535] text-[#4ade80]">
                          <ShoppingBag className="h-12 w-12" />
                        </div>
                      )}
                      <CardContent className="space-y-4 p-5">
                        <div>
                          <div className="flex items-start justify-between gap-3">
                            <h2 className="text-xl font-black text-[#e8eaf0]">{product.name}</h2>
                            <p className="shrink-0 rounded-full bg-[#1a3a25] px-3 py-1 text-sm font-black text-[#4ade80]">
                              {formatMoney(product.price, currency)}
                            </p>
                          </div>
                          {product.description ? <p className="mt-2 text-sm leading-6 text-[#6b7a8d]">{product.description}</p> : null}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {product.category ? <Pill>{product.category}</Pill> : null}
                          {product.sku ? <Pill>SKU {product.sku}</Pill> : null}
                          {typeof product.stock === "number" ? <Pill>{product.stock} disponibles</Pill> : null}
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <QuantityControl
                            disabled={soldOut}
                            onDecrease={() => updateQuantity(product, -1)}
                            onIncrease={() => updateQuantity(product, 1)}
                            quantity={quantity}
                          />
                          {soldOut ? <span className="text-sm font-black text-[#ff8a80]">Agotado</span> : null}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </main>

        <aside className="lg:sticky lg:top-6 lg:self-start">
          <Card className="overflow-hidden rounded-[2rem] border-[#1e2838] bg-[#141921] shadow-2xl">
            <div className="bg-[#141921] p-5 text-white">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4ade80]">Pedido</p>
              <h2 className="mt-2 text-2xl font-black">Resumen y WhatsApp</h2>
            </div>
            <CardContent className="space-y-5 p-5">
              {items.length ? (
                <div className="space-y-3">
                  {items.map((item) => (
                    <div className="flex items-start justify-between gap-3 text-sm" key={item.productId}>
                      <div>
                        <p className="font-black text-[#e8eaf0]">{item.name}</p>
                        <p className="text-[#4a556b]">x{item.quantity} · {formatMoney(item.unitPrice, currency)}</p>
                      </div>
                      <p className="font-black text-[#e8eaf0]">{formatMoney(item.lineTotal, currency)}</p>
                    </div>
                  ))}
                  <div className="border-t border-[#1e2838] pt-3">
                    <div className="space-y-1 text-sm text-[#6b7a8d]">
                      <div className="flex items-center justify-between">
                        <span>Subtotal</span>
                        <span>{formatMoney(quotedSubtotal, currency)}</span>
                      </div>
                      {hasQuoteAdjustments ? (
                        <>
                          {quotedDelivery ? <div className="flex items-center justify-between"><span>Domicilio</span><span>{formatMoney(quotedDelivery, currency)}</span></div> : null}
                          {quotedTax ? <div className="flex items-center justify-between"><span>Impuestos</span><span>{formatMoney(quotedTax, currency)}</span></div> : null}
                          {quotedDiscount ? <div className="flex items-center justify-between"><span>Descuento</span><span>-{formatMoney(quotedDiscount, currency)}</span></div> : null}
                        </>
                      ) : null}
                      <div className="flex items-center justify-between pt-2 text-lg font-black text-[#e8eaf0]">
                        <span>Total final</span>
                        <span>{formatMoney(finalTotal, currency)}</span>
                      </div>
                      {quoteState === "loading" ? <p className="text-xs font-semibold text-[#4a556b]">Validando stock y valores...</p> : null}
                      {quoteState === "error" ? <p className="text-xs font-semibold text-[#fbbf24]">{quoteError || "No pudimos validar el total final; se confirmará al preparar el pedido."}</p> : null}
                    </div>
                  </div>
                  <Button className="h-10 w-full rounded-full" onClick={clearCart} type="button" variant="outline">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Vaciar carrito
                  </Button>
                </div>
              ) : (
                <div className="rounded-3xl border border-dashed border-[#2a3346] bg-[#1e2535] p-5 text-center text-sm text-[#4a556b]">
                  Agrega productos para construir el mensaje de pedido.
                </div>
              )}

              <div className="space-y-3">
                <Field label="Nombre">
                  <Input
                    autoComplete="name"
                    className="border-[#1e2838] bg-[#141921] text-[#e8eaf0]"
                    maxLength={160}
                    onChange={(event) => setCustomer({ ...customer, name: event.target.value })}
                    placeholder="Tu nombre"
                    value={customer.name}
                  />
                  {nameError ? <p className="text-xs font-semibold text-[#ff8a80]">{nameError}</p> : null}
                </Field>
                <Field label="Celular">
                  <Input
                    autoComplete="tel"
                    className="border-[#1e2838] bg-[#141921] text-[#e8eaf0]"
                    inputMode="tel"
                    maxLength={32}
                    onChange={(event) => setCustomer({ ...customer, phone: event.target.value })}
                    placeholder="+57 300 000 0000"
                    type="tel"
                    value={customer.phone}
                  />
                  {phoneError ? <p className="text-xs font-semibold text-[#ff8a80]">{phoneError}</p> : null}
                </Field>
                <Field label="Entrega">
                  <div className="grid grid-cols-2 gap-2">
                    {(["delivery", "pickup"] as const).map((method) => (
                      <button
                        className={`rounded-2xl border px-3 py-2 text-sm font-black ${
                          customer.deliveryMethod === method
                            ? "border-[#4ade80] bg-[#4ade80] text-[#0f1420]"
                            : "border-[#1e2838] bg-[#141921] text-[#6b7a8d]"
                        }`}
                        key={method}
                        onClick={() => setCustomer({ ...customer, deliveryMethod: method })}
                        type="button"
                      >
                        {method === "delivery" ? "Domicilio" : "Recoger"}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Dirección">
                  <Input
                    autoComplete="street-address"
                    className="border-[#1e2838] bg-[#141921] text-[#e8eaf0]"
                    maxLength={240}
                    onChange={(event) => setCustomer({ ...customer, address: event.target.value })}
                    placeholder={addressRequired ? "Barrio, dirección o referencia" : "Opcional si recoges en tienda"}
                    value={customer.address}
                  />
                  {missingAddress ? <p className="text-xs font-semibold text-[#ff8a80]">La dirección es obligatoria para domicilio.</p> : null}
                </Field>
                <Field label="Notas">
                  <Textarea
                    className="min-h-20 border-[#1e2838] bg-[#141921] text-[#e8eaf0]"
                    maxLength={500}
                    onChange={(event) => setCustomer({ ...customer, notes: event.target.value })}
                    placeholder="Ej: sin cebolla, pago por transferencia..."
                    value={customer.notes}
                  />
                </Field>
                <label className="flex items-start gap-3 rounded-2xl border border-[#1e2838] bg-[#1e2535] p-3 text-sm leading-5 text-[#6b7a8d]">
                  <input
                    checked={customer.marketingOptIn}
                    className="mt-1 h-4 w-4 rounded border-[#2a3346]"
                    onChange={(event) => setCustomer({ ...customer, marketingOptIn: event.target.checked })}
                    type="checkbox"
                  />
                  <span>Autorizo recibir confirmaciones, recordatorios y promociones de este negocio por WhatsApp. Puedo pedir que me retiren cuando quiera.</span>
                </label>
              </div>

              <div className="space-y-3 rounded-3xl bg-[#1e2535] p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#4a556b]">Mensaje copiable</p>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-2xl bg-[#141921] p-3 text-xs leading-5 text-[#6b7a8d]">
                  {activeWhatsappMessage || "Selecciona productos para generar el mensaje."}
                </pre>
                <Button className="h-11 w-full rounded-full" disabled={!canCopyMessage} onClick={copyMessage} type="button" variant="outline">
                  {copyState === "copied" ? <Check className="mr-2 h-4 w-4" /> : <Clipboard className="mr-2 h-4 w-4" />}
                  {copyState === "copied" ? "Copiado" : copyState === "failed" ? "No se pudo copiar" : canCopyMessage ? "Copiar mensaje confirmado" : "Primero prepara el pedido"}
                </Button>
              </div>

              <Button
                className="h-12 w-full rounded-full bg-[#4ade80] font-black text-[#0f1420] hover:bg-[#4ade80]/90"
                disabled={!canSubmit || submitState === "submitting"}
                onClick={handleOrder}
                type="button"
              >
                {submitState === "submitting" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Preparar pedido y pago
              </Button>
              <a className="block" href={canOpenWhatsApp ? whatsappLink : undefined} onClick={(event) => { if (!canOpenWhatsApp) event.preventDefault(); }} rel="noreferrer" target="_blank">
                <Button className="h-12 w-full rounded-full bg-[#25d366] font-black text-[#e8eaf0] hover:bg-[#7af0a0]" disabled={!canOpenWhatsApp} type="button">
                  {canOpenWhatsApp ? "Abrir WhatsApp" : !hasMerchantWhatsApp ? "WhatsApp del negocio no configurado" : "Primero prepara el pedido"}
                </Button>
              </a>
              {submitState === "sent" ? (
                <div className="space-y-2 rounded-2xl bg-[#1a3a25] p-3 text-sm font-semibold text-[#4ade80]">
                  <p>{serverOrderMeta.checkoutStatus === "authorized" ? "Pedido listo y pago autorizado." : "Pedido listo. Puedes pagar, copiarlo o abrir WhatsApp para enviarlo."}</p>
                  {serverOrderMeta.orderId ? <p>Orden: {serverOrderMeta.orderId}</p> : null}
                  {serverOrderMeta.provider ? <p>Proveedor: {serverOrderMeta.provider}</p> : null}
                  {serverOrderMeta.paymentInstructions ? <p>{serverOrderMeta.paymentInstructions}</p> : null}
                  {serverOrderMeta.paymentCheckoutError ? <p>No pudimos generar link automático: {serverOrderMeta.paymentCheckoutError}</p> : null}
                  {serverOrderMeta.paymentLink ? (
                    <a className="inline-flex rounded-full bg-[#141921] px-3 py-1 text-[#4ade80] underline" href={serverOrderMeta.paymentLink} rel="noreferrer" target="_blank">
                      Pagar ahora
                    </a>
                  ) : null}
                  {serverOrderMeta.orderStatusUrl ? (
                    <a className="inline-flex rounded-full bg-[#141921] px-3 py-1 text-[#4ade80] underline" href={serverOrderMeta.orderStatusUrl} rel="noreferrer" target="_blank">
                      Ver estado del pedido
                    </a>
                  ) : null}
                </div>
              ) : null}
              {submitState === "error" ? (
                <p className="rounded-2xl bg-[#2a1215] p-3 text-sm font-semibold text-[#ff8a80]">
                  {submitErrorMessage || "No se pudo registrar el pedido. Corrige los datos o intenta de nuevo antes de contactar por WhatsApp."}
                </p>
              ) : null}
            </CardContent>
          </Card>
        </aside>
      </div>
    </StoreShell>
  );
}

function StoreShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[#0f1420] text-[#e8eaf0]">
      <div className="pointer-events-none fixed inset-0 opacity-80">
        <div className="absolute left-[-12rem] top-[-10rem] h-96 w-96 rounded-full bg-[#4ade80]/20 blur-3xl" />
        <div className="absolute bottom-[-12rem] right-[-10rem] h-96 w-96 rounded-full bg-[#fbbf24]/20 blur-3xl" />
      </div>
      <div className="relative">{children}</div>
    </div>
  );
}

function EmptyState({
  actionLabel,
  body,
  icon,
  onAction,
  title,
}: {
  actionLabel?: string;
  body: string;
  icon: React.ReactNode;
  onAction?: () => void;
  title: string;
}) {
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-2xl items-center justify-center px-4">
      <div className="rounded-[2rem] border border-[#1e2838] bg-[#141921] p-8 text-center shadow-2xl">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-[#1a3a25] text-[#4ade80]">{icon}</div>
        <h1 className="mt-5 text-3xl font-black text-[#e8eaf0]">{title}</h1>
        <p className="mt-3 text-sm leading-6 text-[#6b7a8d]">{body}</p>
        {actionLabel && onAction ? (
          <Button className="mt-6 rounded-full bg-[#4ade80] px-6 text-[#0f1420] hover:bg-[#4ade80]/90" onClick={onAction} type="button">
            <RefreshCw className="mr-2 h-4 w-4" />
            {actionLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function QuantityControl({
  disabled,
  onDecrease,
  onIncrease,
  quantity,
}: {
  disabled: boolean;
  onDecrease: () => void;
  onIncrease: () => void;
  quantity: number;
}) {
  return (
    <div className="flex items-center rounded-full border border-[#1e2838] bg-[#1e2535] p-1">
      <button
        aria-label="Quitar producto"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-[#141921] text-[#e8eaf0] shadow-sm disabled:opacity-40"
        disabled={quantity === 0}
        onClick={onDecrease}
        type="button"
      >
        <Minus className="h-4 w-4" />
      </button>
      <span className="min-w-10 text-center text-sm font-black text-[#e8eaf0]">{quantity}</span>
      <button
        aria-label="Agregar producto"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-[#4ade80] text-white shadow-sm disabled:opacity-40"
        disabled={disabled}
        onClick={onIncrease}
        type="button"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}

function Field({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-black uppercase tracking-[0.18em] text-[#4a556b]">{label}</Label>
      {children}
    </div>
  );
}

function Badge({ children, tone = "default" }: { children: React.ReactNode; tone?: "default" | "success" | "warning" }) {
  const styles = {
    default: "border-white/20 bg-[#141921]/15 text-white",
    success: "border-emerald-200/40 bg-[#1a3a25]/20 text-emerald-50",
    warning: "border-[#fbbf24]/50 bg-[#2a2a18]/25 text-[#fbbf24]",
  };
  return <span className={`rounded-full border px-3 py-1 ${styles[tone]}`}>{children}</span>;
}

function Pill({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full bg-[#1e2535] px-3 py-1 text-xs font-bold text-[#6b7a8d]">{children}</span>;
}

function formatMoney(value: number, currency: string) {
  return new Intl.NumberFormat("es-CO", {
    currency,
    maximumFractionDigits: 0,
    style: "currency",
  }).format(value);
}

function buildWhatsappMessage(
  store: PublicTienditaStoreData,
  customer: PublicTienditaCustomer,
  items: PublicTienditaOrderItem[],
  subtotal: number,
) {
  const itemLines = items.length
    ? items.map((item) => `- ${item.quantity} x ${item.name}: ${formatMoney(item.lineTotal, store.currency || "COP")}`).join("\n")
    : "- Sin productos seleccionados";
  const delivery = customer.deliveryMethod === "delivery" ? "Domicilio" : "Recoger en tienda";
  return [
    `Hola ${store.name}, quiero hacer este pedido:`,
    "",
    itemLines,
    "",
    `Total: ${formatMoney(subtotal, store.currency || "COP")}`,
    "",
    `Nombre: ${customer.name || "Pendiente"}`,
    `Celular: ${customer.phone || "Pendiente"}`,
    `Entrega: ${delivery}`,
    `Dirección: ${customer.address || "Pendiente"}`,
    customer.notes ? `Notas: ${customer.notes}` : "Notas: Ninguna",
  ].join("\n");
}

function buildWhatsappLink(phone: string, message: string) {
  const normalizedPhone = phone.replace(/[^\d]/g, "");
  if (!normalizedPhone || !message.trim()) return "";
  return `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(message)}`;
}

function buildPublicOrderIdempotencyKey(payload: PublicTienditaOrderPayload) {
  return `web-order-${simpleHash(JSON.stringify({ slug: payload.slug, phone: payload.customer.phone, delivery: payload.customer.deliveryMethod, address: payload.customer.address, items: payload.items.map((item) => [item.productId, item.quantity]) }))}`;
}

function buildCheckoutIdempotencyKey(slug = "", orderId = "", identity = "") {
  return `web-checkout-${simpleHash(`${slug}:${orderId}:${identity}`)}`;
}

function simpleHash(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function getOrderStatusToken() {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  return params.get("t") || params.get("token") || params.get("statusToken") || "";
}

export default PublicTienditaStore;

export function PublicTienditaOrderStatus() {
  const params = useParams<{ slug: string; orderId: string }>();
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState<Record<string, unknown> | undefined>();
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [checkoutState, setCheckoutState] = useState<"idle" | "loading" | "error">("idle");
  const [error, setError] = useState("");
  const statusToken = getOrderStatusToken();

  const load = () => {
    if (!params.slug || !params.orderId) return;
    setState("loading");
    setError("");
    getPublicTienditaOrderStatus(params.slug, params.orderId, phone, statusToken)
      .then((result) => {
        setStatus(result);
        setState("ready");
      })
      .catch((nextError: unknown) => {
        setError(nextError instanceof Error ? nextError.message : "No pudimos cargar el pedido.");
        setState("error");
      });
  };

  useEffect(() => {
    load();
  }, [params.slug, params.orderId]);

  const order = status?.order as Record<string, unknown> | undefined;
  const events = Array.isArray(status?.events) ? status.events as Array<Record<string, unknown>> : [];
  const paymentLink = order ? String(order.paymentLink || "") : "";

  const startCheckout = async () => {
    if (!params.slug || !params.orderId || !order) return;
    try {
      setCheckoutState("loading");
      const checkout = await createPublicTienditaOrderCheckout(params.slug, params.orderId, { customerPhone: phone, statusToken, idempotencyKey: buildCheckoutIdempotencyKey(params.slug, params.orderId, phone || statusToken) });
      setStatus((current) => ({
        ...(current || {}),
        order: checkout.order || { ...order, paymentLink: checkout.paymentLink || checkout.checkoutUrl },
      }));
      setCheckoutState("idle");
      const link = String(checkout.paymentLink || checkout.checkoutUrl || "");
      if (link) window.open(link, "_blank", "noopener,noreferrer");
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "No pudimos generar el link de pago.");
      setCheckoutState("error");
    }
  };

  return (
    <StoreShell>
      <div className="mx-auto flex min-h-[70vh] max-w-2xl items-center justify-center px-4 py-10">
        <Card className="w-full rounded-[2rem] border-[#1e2838] bg-[#141921] shadow-2xl">
          <CardContent className="space-y-5 p-6">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#4ade80]">Estado del pedido</p>
              <h1 className="mt-2 text-3xl font-black text-[#e8eaf0]">{params.orderId}</h1>
              <p className="mt-2 text-sm leading-6 text-[#6b7a8d]">Consulta el estado con el negocio. Si el comercio pide validar celular, escríbelo abajo.</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
              <Input className="border-[#1e2838] bg-[#141921] text-[#e8eaf0]" onChange={(event) => setPhone(event.target.value)} placeholder="Celular opcional" value={phone} />
              <Button className="rounded-full bg-[#4ade80] px-6 text-[#0f1420] hover:bg-[#4ade80]/80" disabled={state === "loading"} onClick={load} type="button">
                {state === "loading" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Actualizar
              </Button>
            </div>
            {state === "error" ? <p className="rounded-2xl bg-[#2a1215] p-3 text-sm font-semibold text-[#ff8a80]">{error}</p> : null}
            {order ? (
              <div className="space-y-3 rounded-3xl bg-[#1e2535] p-4">
                <p className="text-lg font-black text-[#e8eaf0]">Pedido {String(order.status || "pendiente")}</p>
                <p className="text-sm text-[#6b7a8d]">Pago: {String(order.paymentStatus || "pendiente")}</p>
                <p className="text-sm text-[#6b7a8d]">Total: {formatMoney(Number(order.totalCents || 0) / 100, String(order.currency || "COP"))}</p>
                {paymentLink && String(order.paymentStatus || "") !== "paid" ? (
                  <a className="inline-flex rounded-full bg-[#4ade80] px-4 py-2 text-sm font-black text-[#0f1420] hover:bg-[#4ade80]/80" href={paymentLink} rel="noreferrer" target="_blank">
                    Pagar pedido
                  </a>
                ) : null}
                {!paymentLink && String(order.paymentStatus || "") !== "paid" && String(order.status || "") !== "cancelled" ? (
                  <Button className="rounded-full bg-[#4ade80] px-4 py-2 text-sm font-black text-[#0f1420] hover:bg-[#4ade80]/80" disabled={checkoutState === "loading"} onClick={startCheckout} type="button">
                    {checkoutState === "loading" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Generar link de pago
                  </Button>
                ) : null}
                {events.length ? (
                  <div className="space-y-2 border-t border-[#1e2838] pt-3">
                    {events.map((event) => (
                      <div className="text-xs text-[#6b7a8d]" key={String(event.id)}>
                        {String(event.createdAt || "")}: {String(event.toStatus || "")} / {String(event.toPaymentStatus || "")}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : state === "loading" ? <p className="text-sm text-[#6b7a8d]">Cargando...</p> : null}
          </CardContent>
        </Card>
      </div>
    </StoreShell>
  );
}

async function loadStoreFromApi(slug: string): Promise<PublicTienditaStoreData> {
  const response = await getPublicTienditaCatalog(slug);
  const store = response.store || {};
  const products = response.products || [];
  const deliveryZones = Array.isArray(store.deliveryZones) ? store.deliveryZones.map(String).filter(Boolean) : [];
  return {
    slug: String(store.slug || slug),
    name: String(store.businessName || "Tienda"),
    description: String(store.description || store.businessType || "Catálogo público por WhatsApp"),
    whatsappNumber: String(store.whatsappPhone || ""),
    currency: String(store.currency || "COP"),
    address: store.address ? String(store.address) : undefined,
    deliveryNotes: String(store.deliveryPromise || "") || (deliveryZones.length ? `Zonas: ${deliveryZones.join(", ")}` : undefined),
    status: store.enabled === false ? "paused" : "active",
    products: products.map((product) => {
      const priceCents = Number(product.priceCents || 0);
      return {
        id: String(product.id),
        name: String(product.name || "Producto"),
        description: product.description ? String(product.description) : undefined,
        imageUrl: product.imageUrl ? String(product.imageUrl) : undefined,
        sku: product.sku ? String(product.sku) : undefined,
        available: product.available !== false,
        stock: typeof product.stock === "number" ? product.stock : Number(product.stock || 0),
        price: priceCents / 100,
        category: product.category ? String(product.category) : undefined,
      };
    }),
  };
}

async function submitOrderToApi(payload: PublicTienditaOrderPayload) {
  const normalizedPhone = payload.customer.phone.replace(/[^\d+]/g, "");
  const orderResponse = await createPublicTienditaOrder(payload.slug, {
    customerName: payload.customer.name,
    customerPhone: normalizedPhone,
    deliveryAddress: payload.customer.deliveryMethod === "delivery" ? payload.customer.address : "Recoge en tienda",
    notes: payload.customer.notes,
    marketingOptIn: payload.customer.marketingOptIn,
    consentStatus: payload.customer.marketingOptIn ? "granted" : "pending",
    idempotencyKey: buildPublicOrderIdempotencyKey(payload),
    items: payload.items.map((item) => ({
      productId: item.productId,
      name: item.name,
      quantity: item.quantity,
      price: item.unitPrice,
    })),
  });
  const orderId = String(orderResponse.order?.id || "");
  if (!orderId) return orderResponse;
  try {
    const checkout = await createPublicTienditaOrderCheckout(payload.slug, orderId, {
      customerPhone: payload.customer.phone,
      idempotencyKey: buildCheckoutIdempotencyKey(payload.slug, orderId, payload.customer.phone),
    });
    return {
      ...orderResponse,
      ...checkout,
      order: checkout.order || orderResponse.order,
      whatsappMessage: orderResponse.whatsappMessage,
      paymentInstructions: checkout.checkoutStatus === "authorized"
        ? "Pago autorizado automaticamente. El negocio ya puede preparar tu pedido."
        : orderResponse.paymentInstructions,
      paymentLink: checkout.paymentLink || checkout.checkoutUrl || orderResponse.paymentLink,
    };
  } catch (error) {
    return {
      ...orderResponse,
      paymentCheckoutError: error instanceof Error ? error.message : "No se pudo generar el link de pago.",
    };
  }
}
